/**
 * RFQ / Quotes tab — generate RFQs, click to view/edit each RFQ, compare quotes, confirm selections.
 *
 * Layout: RFQs are per-vendor. Each RFQ card lists its quoted parts with part number,
 * vendor PN, price, and lead time. "Best" is determined per BOM line across ALL vendors
 * (not just within one RFQ), so the badge only appears when this vendor is the best
 * option for that part compared to every other vendor that quoted it.
 *
 * Selection is keyed by job_bom_line ID — one vendor selected per part.
 */

import { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  fetchRfqs, fetchRfq, generateRfqs, sendRfq, updateRfq,
  fetchVendorQuotes, createManualQuote,
  fetchEmailMessage, fetchEmailAttachments, openAttachment,
  assignRfqPo, bulkAssignRfqPo, completeRfqPo,
  resendRfq, toggleAutoResend,
  fetchRfqAttachments, uploadRfqAttachment, deleteRfqAttachment,
} from '../../../api/rfq';
import { prepareEmailReply } from '../../../api/email';
import { Button, LoadingSpinner, EmptyState, Modal, SlideOutPanel, SearchableSelect } from '../../common';
import client from '../../../api/client';
import { useAuth } from '../../../context/AuthContext';
import EmailChatModal from '../../email/EmailChatModal';

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
}

function fmtCurrency(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

const STATUS_STYLES = {
  draft:     'bg-gray-100 text-gray-600',
  sent:      'bg-blue-100 text-blue-700',
  responded: 'bg-green-100 text-green-700',
  selected:  'bg-purple-100 text-purple-700',
  ordered:   'bg-emerald-100 text-emerald-700',
  po_sent:   'bg-emerald-100 text-emerald-700',
  confirmed: 'bg-teal-100 text-teal-700',
  received:  'bg-cyan-100 text-cyan-700',
  closed:    'bg-gray-200 text-gray-500',
};

// Map PO status to display status
const PO_STATUS_DISPLAY = {
  draft: null, // Don't change RFQ status for draft POs
  po_uploaded: 'ordered',
  po_sent: 'po_sent',
  confirmed: 'confirmed',
  partial_confirmed: 'confirmed',
  partial_received: 'received',
  received: 'received',
  closed: 'closed',
};

/**
 * Build a map of job_bom_line_id -> best_quote_id across ALL quotes for this job.
 * "Best" = shortest lead time; ties broken by lowest price.
 */
function buildBestQuoteMap(allQuotes) {
  const byLine = {};
  for (const q of allQuotes) {
    const lineId = q.job_bom_line;
    if (!lineId) continue;
    if (!byLine[lineId]) { byLine[lineId] = []; }
    byLine[lineId].push(q);
  }
  const best = {};
  for (const [lineId, qs] of Object.entries(byLine)) {
    const winner = qs.reduce((b, q) => {
      if (!b) return q;
      const bLead = b.quoted_lead_time_days ?? Infinity;
      const qLead = q.quoted_lead_time_days ?? Infinity;
      if (qLead < bLead) return q;
      if (qLead === bLead) {
        const bPrice = parseFloat(b.quoted_unit_price) ?? Infinity;
        const qPrice = parseFloat(q.quoted_unit_price) ?? Infinity;
        return qPrice < bPrice ? q : b;
      }
      return b;
    }, null);
    if (winner) best[lineId] = winner.id;
  }
  return best;
}

export default function RfqTab({ job, onRefresh }) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [rfqs, setRfqs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [assignDropdown, setAssignDropdown] = useState(null); // rfq.id or 'bulk'


  // RFQ detail slide-out
  const [detailRfq, setDetailRfq] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailAttachments, setDetailAttachments] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  // Manual quote modal
  const [manualQuoteOpen, setManualQuoteOpen] = useState(false);
  const [manualQuoteRfqId, setManualQuoteRfqId] = useState(null);
  const [manualQuoteForm, setManualQuoteForm] = useState({ vendor: '', job_bom_line: '', unit_price: '', lead_time_days: '', notes: '' });

  // Email viewer
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [openingAttachment, setOpeningAttachment] = useState(null); // attachment id currently loading

  // Email Reply Chat Modal
  const [replyModalEntryId, setReplyModalEntryId] = useState(null);
  const [replyModalLoading, setReplyModalLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [rfqData, quoteData] = await Promise.all([
        fetchRfqs({ job: job.id }),
        fetchVendorQuotes({ job: job.id }),
      ]);
      setRfqs(rfqData);
      setQuotes(quoteData);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [job.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load users for assignment picker
  useEffect(() => {
    client.get('/auth/users/').then(({ data }) => {
      setAllUsers(data.data || data.results || data || []);
    }).catch(() => {});
  }, []);

  // ── PO Assignment handlers ─────────────────────────────────
  async function handleAssignPo(rfqId, userId) {
    setAssignDropdown(null);
    try {
      await assignRfqPo(rfqId, userId);
      await loadData();
    } catch (err) { alert(err.response?.data?.error || 'Assign failed'); }
  }

  async function handleBulkAssign(userId) {
    setAssignDropdown(null);
    try {
      await bulkAssignRfqPo(job.id, userId);
      await loadData();
    } catch (err) { alert(err.response?.data?.error || 'Bulk assign failed'); }
  }

  async function handleCompletePo(rfqId, e) {
    e.stopPropagation();
    try {
      await completeRfqPo(rfqId);
      await loadData();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  }

  // ── Generate RFQs ──────────────────────────────────────────────
  const [generateResult, setGenerateResult] = useState(null);

  async function handleGenerate() {
    setActionLoading('generate');
    setGenerateResult(null);
    try {
      const result = await generateRfqs(job.id);
      await loadData();
      onRefresh?.();
      if (result) setGenerateResult(result);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate RFQs');
    } finally {
      setActionLoading(null);
    }
  }

  // ── Poll inbox ─────────────────────────────────────────────────
  async function handlePollInbox() {
    setActionLoading('poll');
    try {
      const api = (await import('../../../api/client')).default;
      await api.post('/email/poll/');
      await loadData();
      onRefresh?.();
    } catch {
      // silently ignore — UI will reflect any new quotes on refresh
    } finally {
      setActionLoading(null);
    }
  }

  // ── Send all drafts ────────────────────────────────────────────
  async function handleSendAll() {
    const drafts = rfqs.filter((r) => r.rfq_status === 'draft');
    if (!drafts.length) return;
    setActionLoading('send-all');
    const failed = [];
    for (const r of drafts) {
      try {
        await sendRfq(r.id);
      } catch {
        failed.push(r.rfq_number || r.id.slice(0, 8));
      }
    }
    await loadData();
    onRefresh?.();
    setActionLoading(null);
    if (failed.length) {
      alert(`Failed to send: ${failed.join(', ')}. Others may have sent \u2014 check the list.`);
    }
  }

  // ── RFQ detail slide-out ───────────────────────────────────────
  async function openDetail(rfqId) {
    setDetailLoading(true);
    setDetailRfq(null);
    setDetailAttachments([]);
    try {
      const rfq = await fetchRfq(rfqId);
      setDetailRfq(rfq);
      setEditSubject(rfq.email_subject || '');
      setEditBody(rfq.email_body || '');
      setEditNotes(rfq.notes || '');
      // Load attachments
      try {
        const atts = await fetchRfqAttachments(rfqId);
        setDetailAttachments(atts);
      } catch { /* ignore */ }
    } catch {
      alert('Failed to load RFQ details');
    } finally {
      setDetailLoading(false);
    }
  }

  function closeDetail() {
    setDetailRfq(null);
    setEditSubject('');
    setEditBody('');
    setEditNotes('');
    setDetailAttachments([]);
  }

  async function handleSaveDetail() {
    if (!detailRfq) return;
    setDetailSaving(true);
    try {
      await updateRfq(detailRfq.id, { email_subject: editSubject, email_body: editBody, notes: editNotes });
      const updated = await fetchRfq(detailRfq.id);
      setDetailRfq(updated);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally {
      setDetailSaving(false);
    }
  }

  async function handleSendFromDetail() {
    if (!detailRfq) return;
    setDetailSaving(true);
    let errorMsg = null;
    try {
      await updateRfq(detailRfq.id, { email_subject: editSubject, email_body: editBody, notes: editNotes });
      await sendRfq(detailRfq.id);
    } catch (err) {
      errorMsg = err.response?.data?.error || 'Failed to send RFQ';
    } finally {
      try { const updated = await fetchRfq(detailRfq.id); setDetailRfq(updated); } catch { /* ignore */ }
      await loadData();
      onRefresh?.();
      setDetailSaving(false);
      if (errorMsg) alert(errorMsg);
    }
  }

  async function handleSendRfq(rfqId, e) {
    e.stopPropagation();
    setActionLoading(`send-${rfqId}`);
    let errorMsg = null;
    try {
      await sendRfq(rfqId);
    } catch (err) {
      errorMsg = err.response?.data?.error || 'Failed to send RFQ';
    } finally {
      await loadData();
      onRefresh?.();
      setActionLoading(null);
      if (errorMsg) alert(errorMsg);
    }
  }

  // ── Manual quote ───────────────────────────────────────────────
  function openManualQuote(rfqId, e) {
    e.stopPropagation();
    setManualQuoteRfqId(rfqId);
    setManualQuoteForm({ vendor: '', job_bom_line: '', list_price: '', unit_price: '', lead_time_days: '', notes: '' });
    setManualQuoteOpen(true);
  }

  async function handleManualQuote() {
    const rfqObj = rfqs.find((r) => r.id === manualQuoteRfqId);
    const payload = {
      job_bom_line: manualQuoteForm.job_bom_line,
      vendor: rfqObj?.vendor || manualQuoteForm.vendor,
      quoted_unit_price: parseFloat(manualQuoteForm.unit_price),
      quoted_lead_time_days: parseInt(manualQuoteForm.lead_time_days) || null,
      notes: manualQuoteForm.notes,
    };
    // Include list_price if vendor uses list pricing
    if (manualQuoteForm.list_price) {
      payload.list_price = parseFloat(manualQuoteForm.list_price);
    }
    try {
      await createManualQuote(payload);
      setManualQuoteOpen(false);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to add quote');
    }
  }

  // ── View original email ────────────────────────────────────────
  async function handleViewEmail(emailId, e) {
    e.stopPropagation();
    setEmailData(null);
    setEmailAttachments([]);
    setEmailModalOpen(true);
    setEmailLoading(true);
    try {
      // Fetch email body first — must succeed.
      // Attachments are best-effort: a failure (e.g. migration not yet run)
      // should NOT block the email body from displaying.
      const msg = await fetchEmailMessage(emailId);
      setEmailData(msg);
      try {
        const atts = await fetchEmailAttachments(emailId);
        setEmailAttachments(atts);
      } catch {
        // Attachments unavailable — show email body without them
        setEmailAttachments([]);
      }
    } catch {
      setEmailData({ error: true });
    } finally {
      setEmailLoading(false);
    }
  }

  async function handleReplyToVendor(emailMessageId, e) {
    e.stopPropagation();
    setReplyModalLoading(true);
    try {
      // Get or create a review queue entry for this email
      const result = await prepareEmailReply(emailMessageId);
      setReplyModalEntryId(result.review_queue_id);
    } catch (err) {
      alert('Failed to prepare reply: ' + (err.response?.data?.error || err.message));
    } finally {
      setReplyModalLoading(false);
    }
  }

  async function handleOpenAttachment(emailId, attId, filename) {
    setOpeningAttachment(attId);
    try {
      await openAttachment(emailId, attId, filename);
    } catch {
      alert('Could not open attachment. It may no longer be available in Gmail.');
    } finally {
      setOpeningAttachment(null);
    }
  }

  // ── Render ─────────────────────────────────────────────────────
  if (loading) return <div className="flex justify-center py-10"><LoadingSpinner /></div>;

  const canEdit = !['complete', 'archived'].includes(job.status);
  const draftCount = rfqs.filter((r) => r.rfq_status === 'draft').length;
  const canGenerateRfqs = !['parts_on_order', 'waiting_on_parts', 'ready_to_build', 'building', 'complete', 'archived'].includes(job.status);

  // Best quote per BOM line across ALL vendors
  const bestQuoteByLine = buildBestQuoteMap(quotes);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-gray-500">
          {rfqs.length} RFQ{rfqs.length !== 1 ? 's' : ''} &middot; {quotes.length} quote{quotes.length !== 1 ? 's' : ''}
          {draftCount > 0 && (
            <span className="ml-2 text-yellow-600 font-medium">{draftCount} unsent draft{draftCount !== 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" loading={actionLoading === 'poll'} onClick={handlePollInbox} title="Check Gmail for new vendor replies">
            📥 Poll Inbox
          </Button>
          {canEdit && (
          <>
          <Button
          size="sm" variant="secondary"
          loading={actionLoading === 'generate'}
          disabled={!canGenerateRfqs}
          onClick={handleGenerate}
          title={!canGenerateRfqs ? 'RFQs already generated — add a new BOM line to re-enable' : 'Generate RFQ drafts for parts that need ordering'}
          >
          Generate RFQs
          </Button>
          {draftCount > 0 && (
          <Button size="sm" variant="secondary" loading={actionLoading === 'send-all'} onClick={handleSendAll}>
          Send All Drafts ({draftCount})
          </Button>
          )}
              {/* Bulk assign PO processing */}
              {rfqs.length > 0 && isAdmin && (
              <div className="relative">
                  <Button
                    size="sm" variant="secondary"
                    onClick={() => setAssignDropdown(assignDropdown === 'bulk' ? null : 'bulk')}
                  >
                    📝 Assign All POs
                  </Button>
                  {assignDropdown === 'bulk' && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setAssignDropdown(null)} />
                      <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1 w-52">
                        <div className="px-3 py-1 text-[10px] text-gray-400 uppercase font-semibold">Assign all to</div>
                        {allUsers.map(u => {
                          const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                          return (
                            <button key={u.id} onClick={() => handleBulkAssign(u.id)}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2">
                              <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[9px] font-bold">
                                {((u.first_name || '')[0] || '') + ((u.last_name || '')[0] || '') || u.email.slice(0, 2).toUpperCase()}
                              </span>
                              <span>{name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Generate RFQs result banner */}
      {generateResult && (
        <div className="rounded-md border p-3 text-sm space-y-1 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <p className="font-medium text-blue-800">
              {generateResult.emails_sent > 0 && `${generateResult.emails_sent} RFQ email${generateResult.emails_sent > 1 ? 's' : ''} sent`}
              {generateResult.emails_sent > 0 && generateResult.no_email_lines > 0 && ' • '}
              {generateResult.no_email_lines > 0 && (
                <span className="text-amber-700">
                  {generateResult.no_email_lines} item{generateResult.no_email_lines > 1 ? 's' : ''} skipped (no vendor email)
                </span>
              )}
              {generateResult.online_quotes_fetched > 0 && ` • ${generateResult.online_quotes_fetched} online quote${generateResult.online_quotes_fetched > 1 ? 's' : ''} fetched`}
              {!generateResult.emails_sent && !generateResult.no_email_lines && !generateResult.online_quotes_fetched && 'No new RFQs needed'}
            </p>
            <button onClick={() => setGenerateResult(null)} className="text-blue-400 hover:text-blue-600 text-xs">✕</button>
          </div>
          {generateResult.no_email_lines > 0 && (
            <p className="text-xs text-amber-600">
              Items without vendor email are marked as "Needs Pulling" on the BOM.
              Order through TrulinX and upload PO, or receive from stock when arrived.
            </p>
          )}
          {generateResult.unassigned_lines > 0 && (
            <p className="text-xs text-gray-500">
              {generateResult.unassigned_lines} line{generateResult.unassigned_lines > 1 ? 's' : ''} skipped (no vendor part assigned)
            </p>
          )}
          {(generateResult.email_errors || []).filter(e => !e.includes('No email')).length > 0 && (
            <p className="text-xs text-red-600">
              Errors: {generateResult.email_errors.filter(e => !e.includes('No email')).join('; ')}
            </p>
          )}
        </div>
      )}

      {/* RFQ list — one card per vendor */}
      {rfqs.length === 0 ? (
        <EmptyState
          title="No RFQs yet"
          description="Click 'Generate RFQs' on the BOM tab or above to create vendor quote requests."
        />
      ) : (
        <div className="space-y-3">
          {rfqs.map((rfq) => {
            const rfqQuotes = quotes.filter((q) => q.rfq === rfq.id);
            // Check if PO exists and what status it's in
            const poStatus = rfq.purchase_order_status;
            const poDisplayStatus = poStatus ? PO_STATUS_DISPLAY[poStatus] : null;
            const isOrdered = rfq.has_purchase_order && poDisplayStatus;
            const displayStatus = isOrdered ? poDisplayStatus : rfq.rfq_status;

            return (
              <div
                key={rfq.id}
                className={`rounded-lg border transition-all cursor-pointer ${
                  isOrdered
                    ? 'bg-emerald-50 border-emerald-300 hover:border-emerald-400 hover:shadow-sm'
                    : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
                }`}
                onClick={() => openDetail(rfq.id)}
              >
                {/* RFQ header */}
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    <div className="min-w-0">
                      <span className="font-medium text-foreground">RFQ #{rfq.rfq_number || rfq.id.slice(0, 8)}</span>
                      <span className="ml-2 text-sm text-gray-500">{rfq.vendor_name || 'Unknown vendor'}</span>
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[displayStatus] || 'bg-gray-100 text-gray-600'}`}>
                        {displayStatus === 'po_sent' ? 'PO sent' : displayStatus}
                      </span>
                      {rfq.purchase_order_number && (
                        <span className="ml-2 px-1.5 py-0.5 bg-emerald-600 text-white text-xs rounded font-medium">
                          PO #{rfq.purchase_order_number}
                        </span>
                      )}
                      {rfq.sent_at && !isOrdered && (
                        <span className="ml-2 text-xs text-gray-400">Sent {fmtDate(rfq.sent_at)}</span>
                      )}
                      {rfq.needs_response && (
                        <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium animate-pulse">
                          ⚠️ Needs Response
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 ml-4" onClick={(e) => e.stopPropagation()}>
                    {/* PO status / assignment badge */}
                    {isOrdered ? (
                      // PO exists - show status
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-medium" title={`PO Status: ${rfq.purchase_order_status}`}>
                        ✓ On Order
                      </span>
                    ) : rfq.po_completed_at ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-medium">
                        ✓ PO Done
                      </span>
                    ) : rfq.po_assigned_to_display ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-50 border border-purple-200" title={`Assigned to ${rfq.po_assigned_to_display.name}`}>
                        <span className="w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center text-[8px] font-bold">
                          {rfq.po_assigned_to_display.initials}
                        </span>
                        <span className="text-[10px] text-purple-700 font-medium">PO</span>
                        {isAdmin && (
                          <button onClick={() => handleCompletePo(rfq.id, { stopPropagation: () => {} })} className="text-[10px] text-green-600 hover:text-green-800 font-medium ml-0.5" title="Mark PO complete">✓</button>
                        )}
                      </span>
                    ) : isAdmin && rfq.rfq_status !== 'draft' ? (
                      <div className="relative">
                        <button
                          onClick={() => setAssignDropdown(assignDropdown === rfq.id ? null : rfq.id)}
                          className="text-[11px] text-purple-600 hover:text-purple-800 font-medium px-2 py-0.5 rounded border border-purple-200 hover:bg-purple-50"
                        >Assign PO</button>
                        {assignDropdown === rfq.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setAssignDropdown(null)} />
                            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1 w-48">
                              {allUsers.map(u => {
                                const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                                const initials = ((u.first_name || '')[0] || '') + ((u.last_name || '')[0] || '') || u.email.slice(0, 2).toUpperCase();
                                return (
                                  <button key={u.id} onClick={() => handleAssignPo(rfq.id, u.id)}
                                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2">
                                    <span className="w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center text-[9px] font-bold">{initials}</span>
                                    <span>{name}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}
                    {canEdit && rfq.rfq_status === 'draft' && (
                      <Button size="sm" variant="secondary" loading={actionLoading === `send-${rfq.id}`} onClick={(e) => handleSendRfq(rfq.id, e)}>
                        Send
                      </Button>
                    )}
                    {canEdit && rfq.rfq_status === 'sent' && (
                      <button
                        onClick={async (e) => { e.stopPropagation(); try { setActionLoading(`resend-${rfq.id}`); await resendRfq(rfq.id); loadData(); } catch (err) { alert(err.response?.data?.error || 'Resend failed'); } finally { setActionLoading(null); } }}
                        disabled={actionLoading === `resend-${rfq.id}`}
                        className="px-2 py-1 text-xs font-medium text-blue-600 border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-50"
                        title="Resend RFQ email to vendor"
                      >
                        {actionLoading === `resend-${rfq.id}` ? 'Sending...' : `↻ Resend${rfq.resend_count ? ` (${rfq.resend_count})` : ''}`}
                      </button>
                    )}
                    {rfq.rfq_status === 'sent' && (
                      <button
                        onClick={async (e) => { e.stopPropagation(); try { await toggleAutoResend(rfq.id); loadData(); } catch { /* ignore */ } }}
                        className={`px-2 py-1 text-xs font-medium rounded border ${
                          rfq.auto_resend_paused
                            ? 'text-gray-500 border-gray-300 hover:bg-gray-50'
                            : 'text-green-600 border-green-300 hover:bg-green-50'
                        }`}
                        title={rfq.auto_resend_paused ? 'Auto-resend is paused — click to resume' : 'Auto-resend active (24h) — click to pause'}
                      >
                        {rfq.auto_resend_paused ? '▶ Resume' : '⏸ Pause'}
                      </button>
                    )}
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={(e) => openManualQuote(rfq.id, e)}>
                        + Quote
                      </Button>
                    )}
                    <span className="text-xs text-gray-400">
                      {rfq.line_count ?? 0} line{(rfq.line_count ?? 0) !== 1 ? 's' : ''}
                    </span>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>

                {/* Quote rows — one row per part quoted on this RFQ.
                    V2-b R5 Fix 4b — when a responded RFQ has fewer
                    quotes than lines, show a "N not quoted" summary
                    row after the quote rows so partial quotes are
                    obvious at a glance. Kevin can drill into RFQ
                    detail to see which specific lines are missing. */}
                {rfqQuotes.length > 0 && (() => {
                  const lineCount = rfq.line_count ?? 0;
                  const missing = Math.max(0, lineCount - rfqQuotes.length);
                  const showPartial = missing > 0 && (
                    rfq.rfq_status === 'responded' || isOrdered
                  );
                  const colSpan = rfqQuotes.some((q) => q.list_price) ? 9 : 8;
                  return (
                  <div className="overflow-x-auto" onClick={(e) => e.stopPropagation()}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`text-left text-xs text-gray-500 uppercase tracking-wider ${isOrdered ? 'bg-emerald-100/50' : 'bg-gray-50'}`}>
                          <th className="px-3 py-2">In-House PN</th>
                          <th className="px-3 py-2">Vendor PN</th>
                          <th className="px-3 py-2 text-right">Qty to Order</th>
                          {rfqQuotes.some((q) => q.list_price) && <th className="px-3 py-2 text-right">List Price</th>}
                          <th className="px-3 py-2 text-right">Unit Price</th>
                          <th className="px-3 py-2 text-right">Lead Time</th>
                          <th className="px-3 py-2">Received</th>
                          <th className="px-3 py-2">Notes</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {rfqQuotes.map((q) => {
                          const bomLineId = q.job_bom_line;
                          const isBest = bomLineId && bestQuoteByLine[bomLineId] === q.id;
                          return (
                            <tr key={q.id} className={`hover:bg-gray-50 ${isBest ? 'bg-green-50/50' : ''}`}>
                              <td className="px-3 py-2 font-mono text-xs font-medium">
                                {q.internal_part_no || q.part_display || '—'}
                                {isBest && <span className="ml-1 text-[10px] text-green-600 font-bold uppercase">Best</span>}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-500">{q.vendor_part_number || '—'}</td>
                              <td className="px-3 py-2 text-right font-medium">{q.qty_to_order != null ? q.qty_to_order : '—'}</td>
                              {rfqQuotes.some((qq) => qq.list_price) && (
                                <td className="px-3 py-2 text-right text-gray-400">{q.list_price ? fmtCurrency(q.list_price) : '—'}</td>
                              )}
                              <td className="px-3 py-2 text-right">{fmtCurrency(q.quoted_unit_price)}</td>
                              <td className="px-3 py-2 text-right">{q.quoted_lead_time_days != null ? `${q.quoted_lead_time_days}d` : '—'}</td>
                              <td className="px-3 py-2 text-gray-500">{fmtDate(q.received_at || q.created_at)}</td>
                              <td className="px-3 py-2 text-gray-500 truncate max-w-[140px]">{q.notes || ''}</td>
                              <td className="px-3 py-2">
                                {q.email_message && (
                                  <button
                                    onClick={(e) => handleViewEmail(q.email_message, e)}
                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                                    title="View original vendor email"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    View Email
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {showPartial && (
                          <tr className="bg-amber-50/60 border-t border-amber-100">
                            <td colSpan={colSpan} className="px-3 py-2 text-xs italic text-amber-800">
                              <span className="font-semibold">
                                {missing} line{missing !== 1 ? 's' : ''} not quoted
                              </span>
                              <span className="ml-1 text-amber-700">
                                — vendor priced {rfqQuotes.length} of {lineCount}. Open RFQ to see which lines are missing.
                              </span>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  );
                })()}

                {rfqQuotes.length === 0 && !rfq.needs_response && (
                  <div className="px-4 py-3 text-xs text-gray-400 italic">No quotes received yet — click to view/edit and send.</div>
                )}

                {/* Pending Response Section - vendor replied with questions, no quotes extracted */}
                {rfqQuotes.length === 0 && rfq.needs_response && (
                  <div className="px-4 py-3 bg-amber-50 border-t border-amber-200" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-amber-800">Vendor replied with questions</p>
                        {rfq.pending_response_summary && (
                          <p className="text-xs text-amber-700 mt-1 line-clamp-2">{rfq.pending_response_summary}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-2">
                        {rfq.pending_response_email && (
                          <>
                            <button
                              onClick={(e) => handleViewEmail(rfq.pending_response_email, e)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View
                            </button>
                            <button
                              onClick={(e) => handleReplyToVendor(rfq.pending_response_email, e)}
                              disabled={replyModalLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 transition-colors disabled:opacity-50"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                              </svg>
                              {replyModalLoading ? 'Loading...' : 'Reply to Vendor'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── RFQ Detail Slide-Out ───────────────────────────────── */}
      <SlideOutPanel
        open={!!detailRfq || detailLoading}
        onClose={closeDetail}
        title={detailRfq ? `RFQ #${detailRfq.rfq_number}` : 'Loading\u2026'}
        width="max-w-2xl"
      >
        {detailLoading && <div className="flex justify-center py-10"><LoadingSpinner /></div>}

        {detailRfq && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium">Vendor</p>
                <p className="font-medium text-foreground">{detailRfq.vendor_name}</p>
                {detailRfq.vendor_email && <p className="text-xs text-gray-500">{detailRfq.vendor_email}</p>}
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium">Status</p>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[detailRfq.rfq_status] || 'bg-gray-100 text-gray-600'}`}>
                  {detailRfq.rfq_status}
                </span>
                {detailRfq.sent_at && <p className="text-xs text-gray-400 mt-1">Sent {fmtDate(detailRfq.sent_at)}</p>}
              </div>
            </div>

            {detailRfq.lines && detailRfq.lines.length > 0 && (
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium mb-2">Line Items ({detailRfq.lines.length}) — TrulinX PO Info</p>
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                        <th className="px-3 py-2">In-House PN</th>
                        <th className="px-3 py-2">Vendor PN</th>
                        <th className="px-3 py-2 text-right">Qty to Order</th>
                        <th className="px-3 py-2">Need By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {detailRfq.lines.map((line) => (
                        <tr key={line.id}>
                          <td className="px-3 py-2">
                            <span className="font-mono text-xs font-medium">{line.internal_part_no || line.part_display || '—'}</span>
                            {line.part_description && (
                              <p className="text-[10px] text-gray-400 truncate max-w-[180px]" title={line.part_description}>{line.part_description}</p>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">{line.vendor_part_display || line.vendor_part || '—'}</td>
                          <td className="px-3 py-2 text-right font-medium">{line.qty_to_order ?? line.qty_requested}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(line.target_delivery_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs text-gray-400 uppercase font-medium mb-1">Email Subject</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
                placeholder="Auto-generated from RFQ data…"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-400 uppercase font-medium mb-1">Email Body</label>
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                rows={14}
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                placeholder="Auto-generated from RFQ data…"
              />
              <p className="text-xs text-gray-400 mt-1">Edit before sending. Changes are saved when you click Save or Send.</p>
            </div>

            {/* Attachments */}
            <div>
              <label className="block text-xs text-gray-400 uppercase font-medium mb-1">
                Attachments ({detailAttachments.length})
              </label>
              {detailAttachments.length > 0 && (
                <div className="space-y-1 mb-2">
                  {detailAttachments.map(att => (
                    <div key={att.id} className={`flex items-center justify-between px-2 py-1.5 rounded border text-xs ${
                      att.source === 'part_auto' ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate">📎 {att.filename}</span>
                        {att.source === 'part_auto' && (
                          <span className="shrink-0 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
                            Auto — {att.part_number || 'Part doc'}
                          </span>
                        )}
                      </div>
                      {att.source === 'uploaded' && (
                        <button
                          onClick={async () => {
                            try {
                              await deleteRfqAttachment(detailRfq.id, att.id);
                              setDetailAttachments(prev => prev.filter(a => a.id !== att.id));
                            } catch { alert('Delete failed'); }
                          }}
                          className="text-red-400 hover:text-red-600 ml-2 shrink-0"
                          title="Remove attachment"
                        >✕</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <label className={`cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded hover:bg-blue-100 ${
                attachmentUploading ? 'text-gray-400 bg-gray-50 border-gray-200' : 'text-blue-600 bg-blue-50 border-blue-200'
              }`}>
                {attachmentUploading ? 'Uploading…' : '+ Add File'}
                <input type="file" className="hidden" disabled={attachmentUploading}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setAttachmentUploading(true);
                    try {
                      const result = await uploadRfqAttachment(detailRfq.id, file);
                      setDetailAttachments(prev => [...prev, result]);
                    } catch { alert('Upload failed'); }
                    finally { setAttachmentUploading(false); e.target.value = ''; }
                  }}
                />
              </label>
              <p className="text-xs text-gray-400 mt-1">All files shown here will be attached when the RFQ email is sent.</p>
            </div>

            <div>
              <label className="block text-xs text-gray-400 uppercase font-medium mb-1">Internal Notes</label>
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                rows={2}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Optional internal notes (not sent to vendor)"
              />
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
              {canEdit && (detailRfq.rfq_status === 'draft' || detailRfq.rfq_status === 'sent') && (
                <Button loading={detailSaving} onClick={handleSendFromDetail}>
                  {detailRfq.rfq_status === 'sent' ? 'Resend RFQ' : 'Save & Send RFQ'}
                </Button>
              )}
              <Button variant="secondary" loading={detailSaving} onClick={handleSaveDetail}>Save Changes</Button>
              <Button variant="ghost" onClick={closeDetail}>Close</Button>
            </div>
          </div>
        )}
      </SlideOutPanel>

      {/* ── Manual Quote Modal ─────────────────────────────────── */}
      {/* ── Email Viewer Modal ─────────────────────────────────── */}
      <Modal
        open={emailModalOpen}
        onClose={() => { setEmailModalOpen(false); setEmailData(null); setEmailAttachments([]); }}
        title="Original Vendor Email"
        maxWidth="max-w-2xl"
      >
        {emailLoading && (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        )}
        {!emailLoading && emailData?.error && (
          <p className="text-sm text-red-500 py-4">Could not load email. It may have been deleted or is unavailable.</p>
        )}
        {!emailLoading && emailData && !emailData.error && (
          <div className="space-y-3">
            {/* Header metadata */}
            <div className="grid grid-cols-2 gap-3 text-sm bg-gray-50 rounded-md p-3 border border-gray-200">
              <div>
                <span className="text-xs text-gray-400 uppercase font-medium block mb-0.5">From</span>
                <span className="text-gray-800 font-medium">{emailData.sender || '—'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-400 uppercase font-medium block mb-0.5">Received</span>
                <span className="text-gray-800">{emailData.received_at ? fmtDate(emailData.received_at) : '—'}</span>
              </div>
              <div className="col-span-2">
                <span className="text-xs text-gray-400 uppercase font-medium block mb-0.5">Subject</span>
                <span className="text-gray-800">{emailData.subject || '(no subject)'}</span>
              </div>
            </div>

            {/* Parse confidence */}
            {emailData.parse_confidence != null && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Parse confidence:</span>
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                  parseFloat(emailData.parse_confidence) >= 0.8
                    ? 'bg-green-100 text-green-700'
                    : parseFloat(emailData.parse_confidence) >= 0.5
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-red-100 text-red-600'
                }`}>
                  {Math.round(parseFloat(emailData.parse_confidence) * 100)}%
                </span>
                {parseFloat(emailData.parse_confidence) < 0.8 && (
                  <span className="text-xs text-yellow-600">⚠️ Low confidence — verify prices manually</span>
                )}
              </div>
            )}

            {/* Attachments */}
            {emailAttachments.length > 0 && (
              <div>
                <span className="text-xs text-gray-400 uppercase font-medium block mb-1.5">
                  Attachments ({emailAttachments.length})
                </span>
                <div className="flex flex-wrap gap-2">
                  {emailAttachments.map((att) => {
                    const isLoading = openingAttachment === att.id;
                    const sizeKb = att.size_bytes ? `${Math.round(att.size_bytes / 1024)} KB` : null;
                    return (
                      <button
                        key={att.id}
                        disabled={isLoading}
                        onClick={() => handleOpenAttachment(emailData.id, att.id, att.filename)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 bg-white hover:bg-gray-50 hover:border-blue-400 text-sm text-gray-700 transition-colors disabled:opacity-60 disabled:cursor-wait shadow-sm"
                        title={att.is_pdf ? 'Open PDF in new tab' : `Download ${att.filename}`}
                      >
                        {isLoading ? (
                          <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                        ) : att.is_pdf ? (
                          <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM9.5 17.5c-.3 0-.5-.1-.7-.2-.5-.4-.5-1.1-.2-2 .3-.8.9-1.6 1.6-2.3-.4-.9-.7-1.8-.8-2.6-.1-.6 0-1.1.3-1.4.2-.2.4-.3.7-.3.6 0 1 .5 1.2 1.3.2.7.2 1.6 0 2.5.5.9 1.1 1.7 1.8 2.3.7-.1 1.3-.2 1.8-.2.9 0 1.4.3 1.4.9 0 .3-.2.6-.5.8-.3.2-.7.2-1.1.2-.6 0-1.3-.2-2-.6-.8.2-1.5.4-2 .7-.2.5-.3.7-.5.9zm.6-1.2c.3-.2.7-.3 1.1-.5-.4-.3-.8-.7-1.1-1.1-.2.5-.3 1-.3 1.3.1.2.2.3.3.3zm2.4-5.3c.1-.5.1-1 0-1.4-.1-.3-.2-.5-.3-.5-.1 0-.2.1-.2.3-.1.4 0 .9.2 1.5l.3.1zm-1.1 3c.3.4.6.7 1 1 .4-.6.7-1.2.9-1.8-.7-.5-1.3-1.1-1.8-1.7-.1.8 0 1.7-.1 2.5zm4.4.7c.3-.1.5-.2.5-.4 0-.1-.1-.2-.5-.2-.3 0-.7.1-1.2.2.4.2.8.4 1.2.4z"/>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                        )}
                        <span className="truncate max-w-[180px]">{att.filename}</span>
                        {sizeKb && <span className="text-xs text-gray-400 flex-shrink-0">{sizeKb}</span>}
                        {att.is_pdf && !isLoading && (
                          <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Email body */}
            <div>
              <span className="text-xs text-gray-400 uppercase font-medium block mb-1">Email Body</span>
              <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-white border border-gray-200 rounded-md p-3 max-h-80 overflow-y-auto font-sans leading-relaxed">
                {emailData.body_text || '(empty body)'}
              </pre>
            </div>

            <div className="flex justify-end pt-1">
              <Button variant="ghost" size="sm" onClick={() => { setEmailModalOpen(false); setEmailData(null); setEmailAttachments([]); }}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={manualQuoteOpen} onClose={() => setManualQuoteOpen(false)} title="Add Manual Quote">
        {(() => {
          const rfqObj = rfqs.find((r) => r.id === manualQuoteRfqId);
          const isListVendor = rfqObj?.vendor_uses_list_pricing;
          const multiplier = rfqObj?.vendor_default_multiplier ? parseFloat(rfqObj.vendor_default_multiplier) : null;
          return (
            <div className="space-y-3">
              {rfqObj && (
                <p className="text-sm text-gray-500">
                  Vendor: <strong>{rfqObj.vendor_name || 'Unknown'}</strong>
                  {isListVendor && multiplier && (
                    <span className="ml-2 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded font-medium">
                      List Price Vendor ×{multiplier}
                    </span>
                  )}
                </p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">BOM Line</label>
                <SearchableSelect
                  value={manualQuoteForm.job_bom_line}
                  onChange={(v) => setManualQuoteForm({ ...manualQuoteForm, job_bom_line: v })}
                  options={(job.bom_summary?.lines_detail || []).map((l) => ({
                    value: l.id,
                    label: `${l.part_display || l.part || l.id.slice(0, 8)} — Qty: ${l.qty_required}`,
                  }))}
                  placeholder="Select BOM line…"
                  emptyMessage="No BOM lines match"
                />
              </div>

              {/* List price vendor: show List Price → auto-calc Your Cost */}
              {isListVendor && multiplier ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">List Price</label>
                      <input
                        type="number" step="0.01"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        placeholder="Vendor's list price"
                        value={manualQuoteForm.list_price}
                        onChange={(e) => {
                          const lp = e.target.value;
                          const yourCost = lp ? (parseFloat(lp) * multiplier).toFixed(4) : '';
                          setManualQuoteForm({ ...manualQuoteForm, list_price: lp, unit_price: yourCost });
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Your Cost <span className="text-gray-400 font-normal">(×{multiplier})</span>
                      </label>
                      <input
                        type="number" step="0.01"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-green-50"
                        value={manualQuoteForm.unit_price}
                        onChange={(e) => setManualQuoteForm({ ...manualQuoteForm, unit_price: e.target.value })}
                      />
                    </div>
                  </div>
                  {manualQuoteForm.list_price && manualQuoteForm.unit_price && (
                    <p className="text-xs text-gray-500">
                      List ${parseFloat(manualQuoteForm.list_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      {' '}× {multiplier}
                      {' '}= <strong className="text-green-700">${parseFloat(manualQuoteForm.unit_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                    </p>
                  )}
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                  <input type="number" step="0.01" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={manualQuoteForm.unit_price} onChange={(e) => setManualQuoteForm({ ...manualQuoteForm, unit_price: e.target.value })} />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Time (days)</label>
                <input type="number" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={manualQuoteForm.lead_time_days} onChange={(e) => setManualQuoteForm({ ...manualQuoteForm, lead_time_days: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" rows={2} value={manualQuoteForm.notes} onChange={(e) => setManualQuoteForm({ ...manualQuoteForm, notes: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={() => setManualQuoteOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={handleManualQuote}>Add Quote</Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Email Reply Chat Modal */}
      {replyModalEntryId && (
        <EmailChatModal
          entryId={replyModalEntryId}
          onClose={() => setReplyModalEntryId(null)}
          onSent={() => {
            setReplyModalEntryId(null);
            loadData(); // Refresh RFQs after sending response
          }}
        />
      )}
    </div>
  );
}
